import { useCallback, useState } from 'react';

/**
 * Per-device store of recently typed speaker names, persisted to localStorage.
 *
 * This replaces the browser's built-in saved-form-data popover (which we
 * suppress with autoComplete="off" to avoid stale/typo suggestions). Because
 * the browser cache is opaque to JS, we keep our own list so the rename field
 * can show a "Recently used" section under the speaker-list suggestions — the
 * best-of-both UX requested by users — and offer an explicit "forget" action.
 */
const STORAGE_KEY = 'scriptix:recent-speaker-names';
const MAX_RECENTS = 15;

const read = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === 'string')
      : [];
  } catch {
    return [];
  }
};

const write = (names: string[]) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(names.slice(0, MAX_RECENTS))
    );
  } catch {
    // Ignore quota / private-mode write failures — recents are best-effort.
  }
};

export const useRecentSpeakerNames = () => {
  const [recents, setRecents] = useState<string[]>(read);

  const remember = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setRecents(prev => {
      const next = [
        trimmed,
        ...prev.filter(n => n.toLowerCase() !== trimmed.toLowerCase())
      ].slice(0, MAX_RECENTS);
      write(next);
      return next;
    });
  }, []);

  const forget = useCallback((name: string) => {
    const target = name.trim().toLowerCase();
    setRecents(prev => {
      const next = prev.filter(n => n.toLowerCase() !== target);
      write(next);
      return next;
    });
  }, []);

  return { recents, remember, forget };
};
